import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu } from '@headlessui/react';
import { EllipsisVerticalIcon, ArrowPathIcon, StopIcon, EyeIcon, TrashIcon } from '@heroicons/react/24/outline';
import { canRetryStep, canCancelPipeline, STEP_STATUSES } from '../../utils/pipelineHelpers';

function PipelineActionsMenu({ pipeline, onRetryStep, onRetryPipeline, onDelete, disabled }) {
    const { t } = useTranslation();
    const [retryingStep, setRetryingStep] = useState(null);

    if (!pipeline) return null;

    const failedSteps = pipeline.steps?.filter(step => 
        step.status === STEP_STATUSES.FAILED && canRetryStep(step)
    ) || [];

    const hasRetryableSteps = failedSteps.length > 0;
    const canCancel = canCancelPipeline(pipeline);

    const handleRetryStep = async (stepName) => {
        setRetryingStep(stepName);
        try {
            await onRetryStep(stepName);
        } finally {
            setRetryingStep(null);
        }
    };

    // Don't render if no actions are available
    if (!hasRetryableSteps && !canCancel && !onRetryPipeline && !onDelete) {
        return null;
    }

    return (
        <Menu as="div" className="relative inline-block text-left">
            <div>
                <Menu.Button 
                    className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black disabled:opacity-50"
                    disabled={disabled}
                >
                    <EllipsisVerticalIcon className="w-5 h-5 text-gray-400" />
                </Menu.Button>
            </div>

            <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                
                {/* Retry Actions */}
                {hasRetryableSteps && (
                    <div className="py-1">
                        <div className="px-4 py-2 text-sm text-gray-500 font-medium">
                            {t('ResearchPipeline.retryFailedSteps')}
                        </div>
                        {failedSteps.map((step) => (
                            <Menu.Item key={step.id}>
                                {({ active }) => (
                                    <button
                                        onClick={() => handleRetryStep(step.step_name)}
                                        disabled={disabled || retryingStep === step.step_name}
                                        className={`${
                                            active ? 'bg-gray-100' : ''
                                        } group flex items-center w-full px-4 py-2 text-sm text-gray-700 disabled:opacity-50`}
                                    >
                                        {retryingStep === step.step_name ? (
                                            <ArrowPathIcon className="w-4 h-4 mr-3 animate-spin" />
                                        ) : (
                                            <ArrowPathIcon className="w-4 h-4 mr-3" />
                                        )}
                                        {t('ResearchPipeline.retryStep')} {step.step_name.replace(/_/g, ' ')}
                                        <span className="ml-auto text-xs text-gray-500">
                                            ({step.retry_count}/{step.max_retries})
                                        </span>
                                    </button>
                                )}
                            </Menu.Item>
                        ))}
                    </div>
                )}

                {/* View Actions */}
                <div className="py-1">
                    <Menu.Item>
                        {({ active }) => (
                            <button
                                onClick={() => window.location.reload()}
                                className={`${
                                    active ? 'bg-gray-100' : ''
                                } group flex items-center w-full px-4 py-2 text-sm text-gray-700`}
                            >
                                <ArrowPathIcon className="w-4 h-4 mr-3" />
                                {t('ResearchPipeline.refreshStatus')}
                            </button>
                        )}
                    </Menu.Item>

                    {onRetryPipeline && (
                        <Menu.Item>
                            {({ active }) => (
                                <button
                                    onClick={onRetryPipeline}
                                    disabled={disabled}
                                    className={`${
                                        active ? 'bg-amber-50' : ''
                                    } group flex items-center w-full px-4 py-2 text-sm text-amber-700 disabled:opacity-50`}
                                >
                                    <ArrowPathIcon className="w-4 h-4 mr-3" />
                                    {t('ResearchPipeline.retryEntireResearch')}
                                </button>
                            )}
                        </Menu.Item>
                    )}

                    {pipeline.status === 'completed' && (
                        <Menu.Item>
                            {({ active }) => (
                                <a
                                    href={`#view-results`} // This would link to results section
                                    className={`${
                                        active ? 'bg-gray-100' : ''
                                    } group flex items-center w-full px-4 py-2 text-sm text-gray-700`}
                                >
                                    <EyeIcon className="w-4 h-4 mr-3" />
                                    {t('ResearchPipeline.viewResults')}
                                </a>
                            )}
                        </Menu.Item>
                    )}
                </div>

                {/* Danger Actions */}
                {(canCancel || onDelete) && (
                    <div className="py-1">
                        {canCancel && (
                            <Menu.Item>
                                {({ active }) => (
                                    <button
                                        onClick={() => {
                                            if (window.confirm(t('ResearchPipeline.confirmCancelResearch'))) {
                                                // This would be handled by parent component
                                                console.log('Cancel research requested');
                                            }
                                        }}
                                        disabled={disabled}
                                        className={`${
                                            active ? 'bg-orange-50' : ''
                                        } group flex items-center w-full px-4 py-2 text-sm text-orange-700 disabled:opacity-50`}
                                    >
                                        <StopIcon className="w-4 h-4 mr-3" />
                                        {t('ResearchPipeline.cancelResearch')}
                                    </button>
                                )}
                            </Menu.Item>
                        )}
                        
                        {onDelete && (
                            <Menu.Item>
                                {({ active }) => (
                                    <button
                                        onClick={onDelete}
                                        disabled={disabled}
                                        className={`${
                                            active ? 'bg-red-50' : ''
                                        } group flex items-center w-full px-4 py-2 text-sm text-red-700 disabled:opacity-50`}
                                    >
                                        <TrashIcon className="w-4 h-4 mr-3" />
                                        {t('ResearchPipeline.deleteResearch')}
                                    </button>
                                )}
                            </Menu.Item>
                        )}
                    </div>
                )}
            </Menu.Items>
        </Menu>
    );
}

export default PipelineActionsMenu;